import { useState } from 'react';
import { X, Facebook, ChevronRight, MapPin, FileText, Sparkles } from 'lucide-react';
import { Client, Vacancy, CopyVariant, Visual } from '../../consultantTypes';

interface FacebookCampaignSetupProps {
  client: Client;
  vacancy: Vacancy;
  approvedCopyVariants: CopyVariant[];
  approvedVisuals: Visual[];
  onClose: () => void;
  onComplete: (campaignData: CampaignData) => void;
}

export interface CampaignData {
  campaignName: string;
  facebookPageId: string;
  location: string;
  leadForm: LeadFormData;
}

export interface LeadFormData {
  greeting: string;
  questions: Array<{
    type: 'SHORT_ANSWER' | 'EMAIL' | 'PHONE';
    label: string;
  }>;
  privacyPolicy: string;
  thankYouMessage: string;
}

export function FacebookCampaignSetup({
  client,
  vacancy,
  approvedCopyVariants,
  approvedVisuals,
  onClose,
  onComplete
}: FacebookCampaignSetupProps) {
  const [step, setStep] = useState<'campaign' | 'location' | 'leadform'>('campaign');

  const defaultCampaignName = `Vacature--${vacancy.title}--${client.name}`;
  const [campaignName, setCampaignName] = useState(defaultCampaignName);
  const [facebookPageId, setFacebookPageId] = useState('');
  const [location, setLocation] = useState('Nederland');

  const [leadForm, setLeadForm] = useState<LeadFormData>({
    greeting: `Interesse in de functie ${vacancy.title}? Vul het formulier in en we nemen contact met je op!`,
    questions: [
      { type: 'SHORT_ANSWER', label: 'Voor- en achternaam' },
      { type: 'EMAIL', label: 'E-mailadres' },
      { type: 'PHONE', label: 'Telefoonnummer' },
      { type: 'SHORT_ANSWER', label: 'Waar woon je?' }
    ],
    privacyPolicy: `${client.name} respecteert je privacy. Je gegevens worden vertrouwelijk behandeld.`,
    thankYouMessage: 'Bedankt voor je interesse! We nemen zo spoedig mogelijk contact met je op.'
  });

  const dummyFacebookPages = [
    { id: 'page_001', name: `${client.name} - Officiële Pagina` },
    { id: 'page_002', name: `${client.name} - Werving & Selectie` },
    { id: 'page_003', name: `${client.name} - Carrière` }
  ];

  const handleGenerateLeadForm = () => {
    setLeadForm({
      greeting: `Hoi! Ben je geïnteresseerd in de functie ${vacancy.title}? Vul je gegevens in en we nemen snel contact op!`,
      questions: [
        { type: 'SHORT_ANSWER', label: 'Volledige naam' },
        { type: 'EMAIL', label: 'E-mailadres' },
        { type: 'PHONE', label: 'Telefoonnummer' },
        { type: 'SHORT_ANSWER', label: 'Woonplaats' },
        { type: 'SHORT_ANSWER', label: 'Beschikbaarheid (bijv. per direct, 1 maand opzegtermijn)' }
      ],
      privacyPolicy: `Bij ${client.name} nemen we privacy serieus. Je gegevens worden enkel gebruikt voor deze sollicitatie.`,
      thankYouMessage: 'Super! We hebben je aanmelding ontvangen en nemen binnen 2 werkdagen contact op.'
    });
  };

  const handleComplete = () => {
    onComplete({
      campaignName,
      facebookPageId,
      location,
      leadForm
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Facebook className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Facebook Campagne Opzetten</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            step === 'campaign' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
          }`}>
            1. Campagne
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            step === 'location' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
          }`}>
            2. Locatie
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
            step === 'leadform' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
          }`}>
            3. Lead Formulier
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 'campaign' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Campagne Instellingen</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Stel de basis campagne-instellingen in. Deze campagne wordt automatisch aangemaakt in Facebook Ads Manager.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campagne Naam
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Standaard formaat: Vacature--[titel]--[bedrijf]</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campagne Doel
                </label>
                <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium">Werkgelegenheid in Nederland</p>
                  <p className="text-xs text-blue-700 mt-1">Geoptimaliseerd voor vacature leads</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecteer Facebook Pagina
                </label>
                <select
                  value={facebookPageId}
                  onChange={(e) => setFacebookPageId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecteer een pagina...</option>
                  {dummyFacebookPages.map(page => (
                    <option key={page.id} value={page.id}>
                      {page.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Campagne Overzicht</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Advertenties:</span>
                    <span className="font-medium text-gray-900">{approvedVisuals.length} visuals</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tekstvarianten:</span>
                    <span className="font-medium text-gray-900">{approvedCopyVariants.length} varianten</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Totaal ads:</span>
                    <span className="font-medium text-gray-900">{approvedVisuals.length * approvedCopyVariants.length} combinaties</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'location' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ad Set: Locatie Targeting</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Configureer de doelgroep locatie voor je advertenties. Er wordt 1 ad set aangemaakt zonder extra doelgroep targeting.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Locatie
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Bijv. Nederland, Amsterdam, Noord-Holland"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Voer een land, regio of stad in</p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Ad Set Instellingen</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li>• 1 Ad Set wordt aangemaakt</li>
                  <li>• Alleen locatie targeting (geen leeftijd/interesse filters)</li>
                  <li>• Alle ads worden in deze ad set geplaatst</li>
                  <li>• Optimalisatie voor conversies (lead formulier inzendingen)</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'leadform' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Lead Formulier</h3>
                  <p className="text-sm text-gray-600">
                    Configureer het formulier dat kandidaten invullen
                  </p>
                </div>
                <button
                  onClick={handleGenerateLeadForm}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  AI Voorstel
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Begroeting
                </label>
                <textarea
                  value={leadForm.greeting}
                  onChange={(e) => setLeadForm({ ...leadForm, greeting: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Vragen
                </label>
                <div className="space-y-3">
                  {leadForm.questions.map((q, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{q.label}</p>
                        <p className="text-xs text-gray-500">{q.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Privacy Verklaring
                </label>
                <textarea
                  value={leadForm.privacyPolicy}
                  onChange={(e) => setLeadForm({ ...leadForm, privacyPolicy: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedankt Bericht
                </label>
                <textarea
                  value={leadForm.thankYouMessage}
                  onChange={(e) => setLeadForm({ ...leadForm, thankYouMessage: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Annuleren
          </button>

          <div className="flex items-center gap-3">
            {step !== 'campaign' && (
              <button
                onClick={() => {
                  if (step === 'location') setStep('campaign');
                  if (step === 'leadform') setStep('location');
                }}
                className="px-5 py-2.5 rounded-lg font-medium text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Vorige
              </button>
            )}

            {step !== 'leadform' ? (
              <button
                onClick={() => {
                  if (step === 'campaign' && facebookPageId) setStep('location');
                  if (step === 'location') setStep('leadform');
                }}
                disabled={step === 'campaign' && !facebookPageId}
                className="px-6 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Volgende
              </button>
            ) : (
              <button
                onClick={handleComplete}
                className="px-6 py-2.5 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Campagne Aanmaken
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
